import { formatValue, humanizeMeasure, formatFromColumn } from "@/lib/reports/labels";

import type { ChartBuildCtx, ChartOption } from "./context";

export function buildFunnelOption({ query, data, palette, base }: ChartBuildCtx): ChartOption {
  // One row per measure, each measure sums its column across all rows.
  // ECharts sort:"descending" lays the largest at the top — works
  // correctly when the measures form an actual funnel hierarchy
  // (sent ≥ opens ≥ replies ≥ meetings). Per-measure label colour
  // assigned in `data` (not the global palette), so each stage gets a
  // distinct, stable hue regardless of legend ordering.
  const previousByMeasure = new Map<string, number>();
  if (data.comparisonRows && data.comparisonRows.length > 0) {
    for (const m of query.measures) {
      previousByMeasure.set(
        m,
        data.comparisonRows.reduce((acc, r) => acc + Number(r[m] ?? 0), 0),
      );
    }
  }
  const series = query.measures.map((m, idx) => {
    const value = data.rows.reduce((acc, r) => acc + Number(r[m] ?? 0), 0);
    return {
      name: humanizeMeasure(m),
      value,
      itemStyle: { color: palette[idx % palette.length] },
    };
  });
  // Per-measure format: counts render as integers, percent rates as
  // percent. Hard-coding "number" produced "153.7 floats sent" on
  // measures that are clearly integer counts but get fractional
  // averages from the mock adapter.
  const formatStage = (m: string | undefined, v: number): string => {
    if (!m) return formatValue(v, "number");
    const fmt = formatFromColumn(m, data.columns);
    if (fmt && fmt !== "number") return formatValue(v, fmt);
    // Integer-by-default for funnel stages — almost all real
    // measures (floats_sent, opens, replies, meetings_booked,
    // profiles_shared) are counts. If the mock returned a non-integer,
    // round so the chart doesn't show "Replies 29.6".
    return formatValue(Math.round(v), "number");
  };
  return {
    ...base,
    tooltip: {
      trigger: "item",
      formatter: (p: { name: string; value: number; dataIndex: number }) => {
        const m = query.measures[p.dataIndex];
        return `<b>${p.name}</b>: ${formatStage(m, p.value)}`;
      },
    },
    legend: { show: true, bottom: 0, textStyle: { color: "#6b7280", fontSize: 11 } },
    series: [
      {
        type: "funnel",
        sort: "descending",
        gap: 4,
        label: {
          show: true,
          position: "inside",
          color: "#fff",
          fontWeight: 700,
          fontSize: 12,
          // White text on coloured bars; outline so it survives when a
          // bar happens to be light (amber, emerald) too.
          textBorderColor: "rgba(17,24,39,0.55)",
          textBorderWidth: 2,
          formatter: (p: { name: string; value: number; dataIndex: number }) => {
            const m = query.measures[p.dataIndex];
            const prev = previousByMeasure.get(m);
            if (prev === undefined || prev === 0) {
              return `${p.name}  ${formatStage(m, p.value)}`;
            }
            const delta = (p.value - prev) / prev;
            const arrow = delta > 0.005 ? "▲" : delta < -0.005 ? "▼" : "■";
            return `${p.name}  ${formatStage(m, p.value)}  ${arrow} ${(delta * 100).toFixed(1)}%`;
          },
        },
        data: series,
      },
    ],
  } as ChartOption;
}
